import { BoxV2 as Box, Flex } from 'portal-commons';
import { FunctionComponent } from 'react';

import { CheckBox } from '@/ui_elements';
import { SelectItem } from '@/utils/types';

interface ChannelSelectorProps {
  error?: boolean;
  note?: string;
  options?: SelectItem[];
  value?: string;
  onChange?: (value: string) => void;
}

interface OptionProps {
  disabled?: boolean;
  checked: boolean;
  option: SelectItem;
  onClick: (value: string) => void;
}

const Container: FunctionComponent = ({ children }) => (
  <Flex sx={{ flexDirection: 'column', gap: 'xxs' }}>{children}</Flex>
);

const SelectorContainer: FunctionComponent = ({ children, ...props }) => (
  <Flex
    sx={{
      flexDirection: 'row',
      alignItems: 'center',
      gap: '48px',
      width: '100%',
      height: '44px',
      padding: '9px 27px',
      background: '#EAF0F2',
    }}
    {...props}
  >
    {children}
  </Flex>
);

const Title: FunctionComponent = ({ children }) => (
  <Box
    sx={{
      width: '116px',
      fontWeight: 600,
      fontSize: '12px',
      color: '#00698F',
    }}
  >
    {children}
  </Box>
);

const Option: FunctionComponent<OptionProps> = ({
  disabled = false,
  checked,
  option,
  onClick,
  ...props
}) => (
  <Flex
    sx={{
      flexDirection: 'row',
      alignItems: 'center',
      gap: '5px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      '& > *': {
        padding: 0,
      },
    }}
    {...props}
    onClick={() => onClick(option.value)}
  >
    <CheckBox
      id={option.value}
      disabled={disabled}
      negative={false}
      type={undefined}
      checked={checked}
      handleCheckbox={() => {
        // Stub function to prevent null access
      }}
    />
    <Box
      sx={{
        fontWeight: 700,
        fontSize: '12px',
        color: checked ? '#00748F' : '#666E71',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {option.label}
    </Box>
  </Flex>
);

const ChannelSelector: FunctionComponent<ChannelSelectorProps> = ({
  error = false,
  note,
  options = [],
  value,
  onChange,
}) => {
  return (
    <Container>
      <SelectorContainer data-testid="channelSelector">
        <Title>Select Channel</Title>
        {options.map((option) => (
          <Option
            key={option.value}
            disabled={option.disabled}
            checked={option.value === value}
            option={option}
            onClick={(value) => {
              if (!option.disabled) {
                onChange?.(value);
              }
            }}
            data-testid={`channelSelector-${option.value}`}
          />
        ))}
      </SelectorContainer>
      {note && (
        <Box
          className="error-msg"
          sx={{
            color: error ? '#B70505' : 't.black60',
            fontWeight: 400,
            fontSize: 'H100',
          }}
          data-testid="helperText"
        >
          {note}
        </Box>
      )}
    </Container>
  );
};

export default ChannelSelector;
